import {
  WSAlert,
  WSButton,
  WSFilters,
  WSInfiniteScroll,
  WSList,
  WSTableItem,
  WSTableToolbar
} from "@wingspanhq/fe-component-library";
import { PayeeRow, WorkLog } from "@wingspanhq/wingspan-api-sdk";
import { useCallback, useMemo, useState } from "react";
import { useSession } from "@hooks/useSession";
import { useQueryWorkLogsSummary } from "../../../../query/workLog/queries/useQueryWorkLogsSummary";
import { WSQueries } from "../../../../query/WSQuery";
import { useUrlQuery } from "../../../../shared/hooks/useUrlQuery";
import { useQueryWorkLogsWithPayees } from "../../../../query/workLog/queries/useQueryWorkLogsWithPayees";
import { useWorkLogBulkActions } from "../../hooks/useWorkLogBulkActions";
import { routeHelpersWorkLogPayerDetails } from "../../paths";
import {
  WorkLogsFilterEmptyState,
  WorkLogsPayerEmptyState
} from "../WorkLogsEmptyStates";
import { useBuildConfig } from "./filters/config";
import { createListRequest } from "./filters/request";
import { Filter } from "./filters/types";
import { TableWorkLogsPayerView } from "./TableWorkLogsPayerView";

type Props = {
  status?: Filter["status"];
  enableBulkActions?: boolean;
};

export const FilterableTableWorkLogsPayerView: React.FC<Props> = props => {
  const { accountId } = useSession();
  const goToPayerDetails = routeHelpersWorkLogPayerDetails.useGoTo();
  const { enableBulkActions } = props;

  const { urlQuery, setUrlQuery } = useUrlQuery<Filter>();

  const request = useMemo(
    () =>
      createListRequest(accountId, {
        status: props.status,
        ...urlQuery.filter
      }),
    [accountId, props.status, urlQuery.filter]
  );

  const filtersConfig = useBuildConfig();

  const queryWorkLogs = useQueryWorkLogsWithPayees(request, {
    joinPayee: true
  });
  const queryWorkLogsSummary = useQueryWorkLogsSummary(request);

  // Build payeesMap from work logs with joined payee data
  const payeesMap = useMemo(() => {
    const map = new Map<string, PayeeRow>();
    (queryWorkLogs.data || []).forEach(workLog => {
      // TODO(v3): payee join keyed on `payeeEngagementId` (v1 used `payeeId`).
      if (workLog.payee && workLog.payeeEngagementId) {
        map.set(workLog.payeeEngagementId, workLog.payee);
      }
    });
    return map;
  }, [queryWorkLogs.data]);

  const [selection, setSelection] = useState<WSTableItem<WorkLog>[]>([]);

  const bulkActions = useWorkLogBulkActions();

  const hasFilters = useMemo(
    () => Object.keys(urlQuery.filter || {}).length > 0,
    [urlQuery.filter]
  );

  const handleBulkReviewAll = useCallback(() => {
    const firstId = queryWorkLogs.data?.[0]?.id;
    if (firstId) {
      goToPayerDetails(
        { workLogId: firstId },
        { search: { bulkReview: "true" } }
      );
    }
  }, [queryWorkLogs.data, goToPayerDetails]);

  const handleApproveSelected = useCallback(() => {
    bulkActions.approve(
      selection.map(i => i.data),
      () => setSelection([])
    );
  }, [selection, bulkActions]);

  const handleReturnSelected = useCallback(() => {
    bulkActions.returnForEditing(
      selection.map(i => i.data),
      () => setSelection([])
    );
  }, [selection, bulkActions]);

  const handleRejectSelected = useCallback(() => {
    bulkActions.reject(
      selection.map(i => i.data),
      () => setSelection([])
    );
  }, [selection, bulkActions]);

  const listSize = queryWorkLogsSummary.data?.listSize;

  return (
    <WSList gap="2XL">
      {enableBulkActions && !!listSize && listSize > 1 && (
        <WSAlert theme="info" title="Bulk actions available">
          Bulk review {listSize.toLocaleString()} work logs or select work logs
          below to take bulk action.{" "}
          <WSButton.Link onClick={handleBulkReviewAll}>
            Bulk review work logs
          </WSButton.Link>
        </WSAlert>
      )}

      {enableBulkActions && selection.length > 0 && (
        <WSTableToolbar
          count={selection.length}
          onClear={() => setSelection([])}
          actionsLabel="Bulk actions"
          actions={[
            {
              label: "Approve",
              icon: "accept",
              helpText:
                "Approve work logs so they are paid in the next payment cycle",
              onClick: handleApproveSelected
            },
            {
              label: "Return for editing",
              icon: "reset",
              helpText:
                'Send work logs back to "In-progress" so they can be corrected',
              onClick: handleReturnSelected
            },
            { separator: true as const },
            {
              label: "Reject",
              icon: "block",
              destructive: true,
              helpText: "Reject the work logs so they are not paid",
              onClick: handleRejectSelected
            }
          ]}
        />
      )}

      <WSFilters<Filter>
        values={urlQuery.filter}
        onFilter={newValue => {
          setUrlQuery({ filter: newValue });
        }}
        primaryFilters={filtersConfig}
        info={{
          count: queryWorkLogsSummary.data?.listSize || 0,
          amount: queryWorkLogsSummary.data?.totalAmount
        }}
      />

      <WSQueries queries={{ queryWorkLogs }}>
        {({ queryWorkLogsData }) =>
          hasFilters && queryWorkLogsData.length === 0 ? (
            <WorkLogsFilterEmptyState />
          ) : queryWorkLogsData.length === 0 ? (
            <WorkLogsPayerEmptyState status={props.status} />
          ) : (
            <WSInfiniteScroll
              mt="M"
              onLoad={() => queryWorkLogs.fetchNextPage()}
              loadMore={queryWorkLogsData.length > 0}
              endOfList={!queryWorkLogs.hasNextPage}
              loading={!!queryWorkLogs.isFetchingNextPage}
            >
              <TableWorkLogsPayerView
                workLogs={queryWorkLogsData}
                payeesMap={payeesMap}
                onRowClick={workLog => {
                  goToPayerDetails({ workLogId: workLog.id! });
                }}
                selection={enableBulkActions ? selection : undefined}
                onSelectionChange={enableBulkActions ? setSelection : undefined}
              />
            </WSInfiniteScroll>
          )
        }
      </WSQueries>
    </WSList>
  );
};
